import React from "react";
import { Link } from "react-router-dom";
import RelatedContentRail from "../components/RelatedContentRail";

const PLAYBOOKS = [
  {
    title: "AI Customer Support Copilot",
    timeline: "4 Weeks",
    outcomes: ["Reduce repetitive ticket load", "Improve first-response speed", "Increase agent consistency"],
    stack: "RAG, LLM routing, quality guardrails",
  },
  {
    title: "Executive Reporting Automation",
    timeline: "3 Weeks",
    outcomes: ["Automate weekly dashboards", "Reduce analyst manual effort", "Standardize narrative summaries"],
    stack: "Data connectors, prompt templates, report workflows",
  },
  {
    title: "Content Acceleration Engine",
    timeline: "5 Weeks",
    outcomes: ["Speed up campaign creation", "Improve channel consistency", "Create reusable creative system"],
    stack: "Brief templates, approval flow, brand-safe generation",
  },
  {
    title: "Internal Knowledge Assistant",
    timeline: "4 Weeks",
    outcomes: ["Faster onboarding", "Better policy access", "Reduced duplicate questions"],
    stack: "Vector search, document ingestion, response evaluation",
  },
];

const RELATED_CONTENT = [
  { to: "/insights", title: "Insights Hub", desc: "See current trends shaping delivery choices." },
  { to: "/ai-readiness", title: "AI Readiness", desc: "Validate if teams are ready to execute." },
  { to: "/templates", title: "Templates Library", desc: "Operationalize each playbook with reusable assets." },
  { to: "/academy", title: "Learning Center", desc: "Upskill delivery teams before large-scale rollout." },
];

export default function Playbooks() {
  return (
    <main className="min-h-screen bg-white">
      <section className="bg-gradient-to-br from-slate-900 via-sky-900 to-indigo-900 text-white px-4 py-20">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs uppercase tracking-[0.24em] text-sky-300 font-bold">Execution Playbooks</p>
          <h1 className="text-4xl sm:text-5xl font-black mt-3 max-w-4xl">Proven implementation paths for high-value AI and automation initiatives.</h1>
          <p className="text-lg text-slate-300 max-w-3xl mt-5">
            These playbooks help teams move from intent to delivery with clear scope, timelines, and expected outcomes.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 gap-6">
          {PLAYBOOKS.map((p) => (
            <article key={p.title} className="border border-gray-200 rounded-2xl p-6 hover:shadow-md transition">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xl font-black text-slate-900">{p.title}</h2>
                <span className="text-xs font-bold uppercase tracking-wider bg-sky-100 text-sky-700 rounded-full px-2 py-1">{p.timeline}</span>
              </div>
              <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">Expected Outcomes</p>
              <ul className="space-y-1.5 mb-4">
                {p.outcomes.map((o) => (
                  <li key={o} className="text-sm text-slate-600 flex items-start gap-2">
                    <span className="text-sky-600 font-bold">•</span>
                    <span>{o}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-slate-500"><span className="font-semibold text-slate-700">Implementation stack:</span> {p.stack}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-16">
          <div className="grid md:grid-cols-3 gap-5">
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <p className="text-2xl font-black text-slate-900">01</p>
              <h3 className="font-bold mt-2 mb-1">Choose One Wedge</h3>
              <p className="text-sm text-slate-600">Start with a narrow workflow that already has measurable volume and pain.</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <p className="text-2xl font-black text-slate-900">02</p>
              <h3 className="font-bold mt-2 mb-1">Pilot with Guardrails</h3>
              <p className="text-sm text-slate-600">Add quality checks, human review gates, and simple observability from day one.</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <p className="text-2xl font-black text-slate-900">03</p>
              <h3 className="font-bold mt-2 mb-1">Scale by Pattern</h3>
              <p className="text-sm text-slate-600">Replicate proven architecture and operating principles across teams.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-16 grid gap-8 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="rounded-2xl bg-gradient-to-r from-sky-600 to-indigo-700 text-white p-8 sm:p-10">
          <h2 className="text-3xl font-black mb-3">Need a custom playbook?</h2>
          <p className="text-sky-100 mb-6 max-w-2xl">We can tailor these playbooks to your team structure, data reality, and business priorities.</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/consulting" className="px-5 py-2.5 rounded-lg bg-white text-sky-700 font-bold hover:bg-sky-50 transition">Talk to Consulting</Link>
            <Link to="/templates" className="px-5 py-2.5 rounded-lg border border-white/40 text-white font-semibold hover:bg-white/10 transition">Browse Templates</Link>
          </div>
        </div>
        <RelatedContentRail section="playbooks" links={RELATED_CONTENT} />
      </section>
    </main>
  );
}
